import classNames from "classnames"

import BorderedIconButton from "./BorderedIconButton"
import { CheckoutContext } from "../../../../checkout/store"
import { useResolvedSolution } from "../../useResolvedSolution"
import type { Answer, SolutionItem } from "../types"
import { getOnlineProductsSignupUrl } from "../utils"

import BulkAddToCartButton from "@components/elements/V2/Buttons/BulkAddToCartButton"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import ClockIcon from "@components/elements/V2/Icons/ClockIcon"
import RotateIcon from "@components/elements/V2/Icons/RotateIcon"
import { Progress } from "@components/elements/V2/ProgressBar"
import Typography from "@components/elements/V2/Typography"
import { ONLINE_SERVICES } from "@constants/experiments"
import { storyblokHorizontalGutterClasses } from "@constants/spacing"
import { Color } from "@constants/V2/color"
import { SelectedPurchasableItem } from "@hooks/V2/storyblok"
import { parseConsolidatedPrice } from "@hooks/V2/storyblok/useConsolidatedItem"
import { trackSegmentEvent } from "@utils/analytics"
import { getBackgroundColorClass, getTextColorClass } from "@utils/V2/color"
import React, { useContext } from "react"

type SolutionNavProps = {
  progress: number
  currentStep: number
  totalQuestions: number
  isMultipleAnswers: boolean
  breadcrumbs: string[]
  multiselectedAnswers: Answer[]
  solution: SolutionItem[]
  showResultActions?: boolean
  onBack?: () => void
  onRestart?: () => void
  onNext?: () => void
}

const SolutionNav = ({
  progress,
  currentStep,
  totalQuestions,
  isMultipleAnswers,
  breadcrumbs,
  multiselectedAnswers,
  solution,
  showResultActions = true,
  onBack,
  onRestart,
  onNext,
}: SolutionNavProps) => {
  const { setChosenServices } = useContext(CheckoutContext)
  const resolvedSolution = useResolvedSolution(solution)

  const purchasableItems = resolvedSolution.flatMap((resolved) =>
    resolved.kind === "purchasable" ? [resolved.consolidatedItem] : []
  )

  const cartItems: SelectedPurchasableItem[] = purchasableItems.map(
    (purchasableItem) => ({ purchasableItem, quantity: 1 })
  )

  const cartTotalPrice = purchasableItems.reduce(
    (total, item) => total + (parseConsolidatedPrice(item.price) ?? 0),
    0
  )

  const getSignupServices = () => {
    return resolvedSolution.flatMap((resolved) => {
      if (resolved.kind === "combinedOnline")
        return Object.keys(ONLINE_SERVICES)

      return resolved.kind === "onlineProduct" ? [resolved.item.value] : []
    })
  }

  const breadcrumbText = [
    ...breadcrumbs,
    ...multiselectedAnswers.map((a) => a.breadcrumb || a.answer),
  ].join(" + ")

  const isOnResults = currentStep > totalQuestions
  const hasPhysicalItems = purchasableItems.length > 0
  const onlineProductsSignupUrl =
    getOnlineProductsSignupUrl(getSignupServices())

  return (
    <>
      {currentStep > 1 && onBack && (
        <div className="flex items-center justify-center gap-20 pt-[10px] sm:hidden">
          <BorderedIconButton
            onClick={onBack}
            icon={<ArrowIcon className="rotate-180" />}
            className="h-[50px] w-[50px]"
          />

          {isOnResults && onRestart && (
            <BorderedIconButton
              onClick={onRestart}
              icon={<RotateIcon />}
              className="h-[50px] w-[50px]"
            />
          )}
        </div>
      )}

      <div
        className={classNames(
          "fixed bottom-0 z-10 h-auto w-full pb-[20px] sm:flex",
          getBackgroundColorClass(Color.White)
        )}
      >
        <Progress value={progress} className="absolute top-0 w-full" />

        {currentStep === 1 && (
          <div className="mt-[30px] flex w-full items-center justify-center">
            <ClockIcon
              className={classNames(
                "mb-auto mr-4 mt-auto",
                getTextColorClass(Color.Blue)
              )}
            />
            <Typography
              text="Quiz time:&nbsp;"
              font="grotesk"
              weight="book"
              size="body-sm"
              color={Color.Black}
            />
            <Typography
              text="30 seconds"
              font="grotesk"
              weight="medium"
              size="body-sm"
              color={Color.Black}
            />
          </div>
        )}

        {currentStep > 1 && (
          <div
            className={classNames(
              "mt-[20px] flex w-full flex-col items-center sm:flex-row",
              storyblokHorizontalGutterClasses
            )}
          >
            {onBack && (
              <div className="hidden w-full items-center sm:order-1 sm:flex sm:w-auto">
                <BorderedIconButton
                  onClick={onBack}
                  icon={<ArrowIcon className="rotate-180" />}
                  text="Back"
                  className="w-full gap-10 px-16 py-10 sm:w-auto"
                />

                {isOnResults && onRestart && (
                  <BorderedIconButton
                    onClick={onRestart}
                    icon={<RotateIcon />}
                    text="Restart"
                    className="mx-10 w-full gap-[6px] px-10 py-8 sm:w-auto"
                  />
                )}

                <Typography
                  text={breadcrumbText}
                  font="grotesk"
                  weight="medium"
                  size="body-sm"
                  color={Color.Black}
                  className="absolute left-1/2 hidden -translate-x-1/2 md:block"
                />
              </div>
            )}

            {!isMultipleAnswers && !isOnResults && (
              <Typography
                text={breadcrumbText}
                font="grotesk"
                weight="medium"
                size="body-sm"
                color={Color.Black}
                className="block sm:hidden"
              />
            )}

            {isMultipleAnswers && !isOnResults && (
              <div className="order-1 ml-auto flex w-full pb-10 sm:order-2 sm:w-auto sm:pb-0">
                <PillButton
                  size="small"
                  style="solid"
                  color={Color.Blue}
                  text="Next"
                  disabled={multiselectedAnswers.length === 0}
                  onClick={onNext}
                />
              </div>
            )}

            {isOnResults && showResultActions && (
              <div className="order-1 ml-auto flex w-full pb-0 sm:order-2 sm:w-auto">
                {hasPhysicalItems ? (
                  <BulkAddToCartButton
                    selectedPurchasableItems={cartItems}
                    size="small"
                    onClick={() => {
                      trackSegmentEvent("Product Finder Adding to Cart")
                      setChosenServices(getSignupServices())
                    }}
                    price={cartTotalPrice}
                  />
                ) : (
                  <PillButton
                    style="solid"
                    size="small"
                    color={Color.Blue}
                    text="Sign up now"
                    disabled={
                      resolvedSolution.length < 1 || !onlineProductsSignupUrl
                    }
                    linkUrl={onlineProductsSignupUrl}
                    onClick={() => trackSegmentEvent("Product Finder Sign up")}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default SolutionNav
